import { Button, Chip, Modal, Select, Slider, TextInput } from '@mantine/core';
import React, { useState } from 'react';
import { useImmer } from 'use-immer';
import { Box, Flex, Text } from '../../../../components';
import { COLUMN_CONSTRAINTS } from '../../../../constants/column';
import { useAppSelector } from '../../../../hooks';
import { ColumnPropertiesI } from '../../../../types/column';
import { PropertiesFormI } from '../../../../types/common';
import { cloneObject } from '../../../../utils';
import {
  dataTypeOptions,
  getColorFromConstraint,
  isStringDataType,
} from '../../../../utils/column';

const ConstraintChip = ({
  constraint,
  onChange,
  checked,
}: {
  constraint: string;
  onChange: () => void;
  checked: boolean;
}) => {
  return (
    <Box mx="16px" mb="16px">
      <Chip
        value={constraint}
        checked={checked}
        onChange={onChange}
        size="md"
        variant="filled"
        color={getColorFromConstraint(constraint)}
      >
        {constraint}
      </Chip>
    </Box>
  );
};

const ColumnPropertiesForm = ({
  onClose,
  onSubmit,
}: PropertiesFormI): JSX.Element => {
  const diagram = useAppSelector((state) => state.diagramR);
  const columnProperties = cloneObject(diagram.selectedElement);

  const initialForm: ColumnPropertiesI = {
    name: columnProperties?.data.name || '',
    data_type: columnProperties?.data.data_type || 'VARCHAR2',
    max_length: columnProperties?.data.max_length || 64,
    default_value: columnProperties?.data.default_value || '',
    constraints: {
      PRIMARY_KEY: columnProperties?.data.constraints.PRIMARY_KEY || false,
      UNIQUE: columnProperties?.data.constraints.UNIQUE || false,
      NOT_NULL: columnProperties?.data.constraints.NOT_NULL || false,
      AUTO_INCREMENT:
        columnProperties?.data.constraints.AUTO_INCREMENT || false,
      FOREIGN_KEY: columnProperties?.data.constraints.FOREIGN_KEY || false,
    },
  };

  const [form, setForm] = useImmer(initialForm);
  const [sliderDisable, setSliderDisable] = useState(
    !isStringDataType(form.data_type),
  );

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((state) => {
      state.name = event.target.value;
    });
  };

  const handleDataTypeChange = (dataType: string) => {
    setForm((state) => {
      state.data_type = dataType;
      if (isStringDataType(dataType)) {
        setSliderDisable(false);
      } else {
        setSliderDisable(true);
      }
    });
  };

  const handleDefaultValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setForm((state) => {
      state.default_value = event.target.value;
    });
  };

  const handleMaxLengthChange = (maxLength: number) => {
    setForm((state) => {
      state.max_length = maxLength;
    });
  };

  const handleConstraintChange = (constraint: string) => {
    setForm((state) => {
      state.constraints[constraint] = !state.constraints[constraint];
    });
  };

  const handleSubmitData = () => {
    columnProperties.data.name = form.name;
    columnProperties.data.default_value = form.default_value;
    columnProperties.data.data_type = form.data_type;
    columnProperties.data.max_length = form.max_length;
    columnProperties.data.constraints = form.constraints;
    onSubmit(columnProperties);
  };

  return (
    <Modal
      opened={diagram.showPropertiesForm}
      onClose={onClose}
      title="Column Properties"
      hideCloseButton
      size="lg"
      transition="fade"
      transitionDuration={600}
      transitionTimingFunction="ease"
      centered
    >
      <Box m="16px">
        <Box mb="16px">
          <TextInput
            size="md"
            value={form.name}
            onChange={handleNameChange}
            label="Column Name"
            placeholder="Enter Column Name"
            required
          />
        </Box>
        <Box mb="16px">
          <Select
            label="Column Data type"
            placeholder="Pick one data type"
            data={dataTypeOptions}
            value={form.data_type}
            onChange={handleDataTypeChange}
            size="md"
            required
          />
        </Box>
        <Box mb="16px">
          <TextInput
            size="md"
            value={form.default_value}
            onChange={handleDefaultValueChange}
            label="Default value"
            placeholder="Enter default column value"
          />
        </Box>
        {sliderDisable ? null : (
          <>
            <Text my="0px" fontSize="17px" mt="2px">
              Max Length
            </Text>

            <Slider
              marks={[
                { value: 32, label: '32' },
                { value: 64, label: '64' },
                { value: 128, label: '128' },
              ]}
              defaultValue={64}
              min={1}
              max={256}
              label={(value) => value}
              value={form.max_length}
              onChange={handleMaxLengthChange}
            />
          </>
        )}
        <Flex justifyContent="center" mt="40px" flexWrap="wrap">
          {Object.keys(COLUMN_CONSTRAINTS).map((constraint) => (
            <ConstraintChip
              key={constraint}
              constraint={constraint}
              onChange={() => handleConstraintChange(constraint)}
              checked={form.constraints[constraint]}
            />
          ))}
        </Flex>
        <Flex justifyContent="center" mt="28px">
          <Button onClick={handleSubmitData} size="md" color="purple">
            Save
          </Button>
        </Flex>
      </Box>
    </Modal>
  );
};

export default ColumnPropertiesForm;
